import { createFileRoute, redirect } from '@tanstack/react-router';
import { Suspense, lazy } from 'react';
import { z } from 'zod';

import { PageSkeleton } from '~/components/common/page-skeleton/page-skeleton';
import { useCatalogExportType } from '~/features/exports/hooks/use-catalog-export-type';
import { useMainScrollReset } from '~/features/exports/hooks/use-main-scroll-reset';

// Explicit dynamic import — see `exports/$exportId.tsx` for why (same shared module graph).
const CatalogExportsListPage = lazy(() =>
  import('~/features/exports/export-profiles/pages/catalog-exports-list-page').then((m) => ({
    default: m.CatalogExportsListPage,
  })),
);

/** Every state the list's own UI owns lives in the URL, so a bookmark/Back restores it exactly:
 *  the status tab, the archived toggle, the source/connector filters, free-text search and the
 *  sortable table's own sort key/direction (prototype `LS` state, made URL-durable). */
const searchSchema = z.object({
  /** Which list the page shows: exports (default), the export profiles, or every schedule. */
  view: z.enum(['exports', 'profiles', 'schedules']).optional().catch('exports'),
  /** The Schedules view's own tab: all schedules (default) or every firing. */
  schedulesTab: z.enum(['all', 'firings']).optional().catch('all'),
  tab: z.enum(['all', 'progress', 'ready', 'attention', 'delivered']).optional().catch('all'),
  archived: z.boolean().optional().catch(false),
  /** `'all' | 'manual' | 'scheduled' | 'runNow' | <profileId>` (`adhoc` = old `manual`). */
  source: z.string().optional().catch('all'),
  connector: z.enum(['all', 'GMC', 'META']).optional().catch('all'),
  /** Profile filter: a profile id; omitted = any profile. */
  profile: z.string().optional().catch(undefined),
  /** Created-by filter: a creator's email/handle; omitted = everyone. */
  creator: z.string().optional().catch(undefined),
  /** File format (`CSV`, `TSV`, …); omitted = any. */
  format: z.string().optional().catch(undefined),
  /** Created within: today, the last 7 or 30 days; omitted = any time. */
  created: z.enum(['any', 'today', '7d', '30d']).optional().catch('any'),
  q: z.string().optional(),
  sort: z.enum(['name', 'status', 'at', 'rows']).optional().catch('at'),
  dir: z.enum(['asc', 'desc']).optional().catch('desc'),
});

/**
 * Catalog exports — the landing page for `/exports/catalog`. Replaces the builder as the entry
 * point (Catalog Operations' "Export full catalog" now opens here; the builder moved to
 * `/exports/catalog/new`). Ungated — the old (pre-redesign) `/exports/catalog` route had no
 * `beforeLoad` at any level, open to every tenant user like the batch export page, and this
 * keeps exactly that access; a feature flag nobody has enabled must never lock out the landing
 * page. Write actions (create/edit a profile, run-now, approve, mark-delivered, archive, …) are
 * permission-gated inline instead, the same model `connections:create/update/delete` uses for
 * other integrations writes — see `usePermission` call sites across `export-profiles/**`.
 *
 * One thing still redirects away from this page, ahead of the component ever mounting: an old
 * deep link into the builder's validation panel (`?validation=...`, from before this list
 * existed) — forwarded to `/exports/catalog/new` with its search intact.
 */
export const Route = createFileRoute('/_authenticated/_tenant/exports/catalog/')({
  validateSearch: searchSchema,
  beforeLoad: ({ search }) => {
    const raw = search as Record<string, unknown>;
    if ('validation' in raw || 'validationBatch' in raw) {
      // eslint-disable-next-line @typescript-eslint/only-throw-error -- TanStack Router redirect
      throw redirect({ to: '/exports/catalog/new', search: raw as never, replace: true });
    }
  },
  component: RouteComponent,
});

function RouteComponent() {
  useCatalogExportType();
  useMainScrollReset();
  return (
    <Suspense fallback={<PageSkeleton />}>
      <CatalogExportsListPage />
    </Suspense>
  );
}
